/**
 * StrategicGroundingPanel — right-column panel on the Strategy / Document lens.
 *
 * Shows:
 *  1. PersonaThreadCard — persona name + one-liner from brief.audience; links to persona page.
 *  2. BuyerScenariosCard — counts unique scenario_ids across selected_angles;
 *     lists scenario names; CTA to Angles tab when none are linked.
 *  3. DecisionArchitectureCard — calls /api/v1/cognitive/ontology-context live;
 *     top concept activations (ATTRIBUTE/NEED/VALUE/TRUST) driving this persona's
 *     decisions. Gracefully degrades when ontology isn't yet built.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, GitBranch, Network, User } from 'lucide-react';
import { agentLabApi, personasApiV2, type BriefAngle, type GenerationBrief } from '@/services/api';

interface Props {
  brief: GenerationBrief;
  /** Called to navigate to the Angles tab. */
  onNavigateToAngles?: () => void;
}

// ── Types for ontology-context response ────────────────────────────────────

interface ConceptActivation {
  concept_id: string;
  display_name: string;
  entity_type: string;
  weight: number;
  effective_weight?: number;
  rel_type?: string;
  relation?: string;
}

interface OntologyContextResponse {
  available: boolean;
  tier?: string;
  fallback_from?: string;
  concept_activations?: ConceptActivation[];
}

function normalizeOntologyContext(raw: Record<string, unknown>): OntologyContextResponse {
  const activations = (raw.concept_activations as Record<string, unknown>[] | undefined) ?? [];
  return {
    available: Boolean(raw.available),
    tier: raw.tier as string | undefined,
    fallback_from: raw.fallback_from as string | undefined,
    concept_activations: activations.map((a) => ({
      concept_id: String(a.concept_id ?? ''),
      display_name: String(a.display_name ?? a.concept_id ?? ''),
      entity_type: String(a.entity_type ?? 'OTHER').toUpperCase(),
      weight: Number(a.weight ?? a.effective_weight ?? a.base_weight ?? 0),
      rel_type: (a.rel_type ?? a.relation) as string | undefined,
    })),
  };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function collectScenarios(brief: GenerationBrief): { ids: Set<string>; names: string[] } {
  const ids = new Set<string>();
  const names: string[] = [];
  for (const angle of brief.selected_angles ?? []) {
    const a = angle as BriefAngle;
    if (a.scenario_id && !ids.has(a.scenario_id)) {
      ids.add(a.scenario_id);
      if (a.scenario_name) names.push(a.scenario_name);
    }
  }
  return { ids, names };
}

const ENTITY_TYPE_COLORS: Record<string, string> = {
  ATTRIBUTE: 'bg-sky-50 text-sky-700 border-sky-200',
  NEED: 'bg-violet-50 text-violet-700 border-violet-200',
  VALUE: 'bg-teal-50 text-teal-700 border-teal-200',
  TRUST: 'bg-amber-50 text-amber-700 border-amber-200',
};

// ── Sub-components ──────────────────────────────────────────────────────────

function PersonaThreadCard({ brief }: { brief: GenerationBrief }) {
  const [kpis, setKpis] = useState<string[] | null>(null);

  const audience = brief.audience as Record<string, unknown> | undefined;
  const identity = (audience?.identity ?? {}) as Record<string, unknown>;
  const demographics = (audience?.demographics ?? {}) as Record<string, unknown>;

  const canonicalSlug = brief.persona_id || '';
  const audienceName =
    (audience?.persona_name as string) ||
    canonicalSlug.replace(/_/g, ' ') ||
    '—';
  const oneLiner = (audience?.one_liner as string) || '';
  const identityStatement = (identity.identity_statement as string) || '';
  const archetypeLabel =
    (identity.archetype_label as string) || (audience?.role as string) || '';
  const ageDist =
    (demographics.age_distribution as string) || (audience?.persona_age as string) || '';
  const genderSplit = (demographics.gender_split as string) || '';

  const personaHref =
    canonicalSlug && brief.client_uid
      ? `/personas/${encodeURIComponent(brief.client_uid)}/${encodeURIComponent(canonicalSlug)}`
      : null;

  // Light fetch for KPI tags — silently no-ops if persona isn't enriched yet
  useEffect(() => {
    if (!brief.client_uid || !canonicalSlug) return;
    let cancelled = false;
    personasApiV2
      .listPersonas(brief.client_uid)
      .then(({ personas }) => {
        if (cancelled) return;
        const found = personas.find(
          (p) => p.slug === canonicalSlug || p.id === canonicalSlug,
        );
        if (found?.kpis?.length) setKpis(found.kpis as string[]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [brief.client_uid, canonicalSlug]);

  const showDescriptionExpanded =
    identityStatement && identityStatement !== oneLiner;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2.5">
      {/* Header row */}
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
          <User size={12} className="text-violet-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
            Persona
          </div>
          <div className="text-sm font-semibold text-slate-800 leading-tight">
            {audienceName}
          </div>
          {canonicalSlug && canonicalSlug !== audienceName.toLowerCase().replace(/ /g, '_') && (
            <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5 inline-block">
              {canonicalSlug}
            </span>
          )}
        </div>
        {archetypeLabel && (
          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide bg-violet-50 text-violet-600 border border-violet-200 px-1.5 py-0.5 rounded leading-tight">
            {archetypeLabel}
          </span>
        )}
      </div>

      {/* Summary */}
      {oneLiner && (
        <p className="text-[11px] text-slate-600 leading-snug">{oneLiner}</p>
      )}
      {showDescriptionExpanded && (
        <p className="text-[11px] text-slate-500 leading-snug italic border-l-2 border-violet-100 pl-2">
          {identityStatement}
        </p>
      )}

      {/* Demographics */}
      {(ageDist || genderSplit) && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {ageDist && (
            <span className="text-[10px] bg-slate-50 text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded">
              {ageDist}
            </span>
          )}
          {genderSplit && (
            <span className="text-[10px] bg-slate-50 text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded">
              {genderSplit}
            </span>
          )}
        </div>
      )}

      {/* KPI tags — loaded from enriched persona */}
      {kpis && kpis.length > 0 && (
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            KPIs
          </div>
          <div className="flex flex-wrap gap-1">
            {kpis.slice(0, 5).map((kpi) => (
              <span
                key={kpi}
                className="text-[9px] bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded-full font-medium"
              >
                {kpi}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      {personaHref && (
        <Link
          to={personaHref}
          className="flex items-center justify-center gap-1.5 w-full px-3 py-1.5 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition mt-1"
        >
          View Persona Profile
          <ArrowUpRight size={11} />
        </Link>
      )}
    </div>
  );
}

function BuyerScenariosCard({
  brief,
  onNavigateToAngles,
}: {
  brief: GenerationBrief;
  onNavigateToAngles?: () => void;
}) {
  const { ids, names } = collectScenarios(brief);
  const scenarioCount = ids.size;
  const angleCount = brief.selected_angles?.length ?? 0;

  return (
    <Card title="Buyer scenarios" icon={GitBranch}>
      {scenarioCount === 0 ? (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-500">
            No buyer scenarios linked yet. Generate angles to connect this brief to your
            decision architecture.
          </p>
          {onNavigateToAngles && (
            <button
              onClick={onNavigateToAngles}
              className="flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-700 transition"
            >
              <GitBranch size={10} />
              Go to Angles →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-700">
            {scenarioCount} scenario{scenarioCount > 1 ? 's' : ''} mapped across{' '}
            {angleCount} angle{angleCount > 1 ? 's' : ''}.
          </p>
          {names.length > 0 && (
            <ul className="space-y-1">
              {names.map((name) => (
                <li
                  key={name}
                  className="flex items-center gap-1.5 text-[11px] text-slate-600"
                >
                  <span className="w-1 h-1 rounded-full bg-teal-400 shrink-0" />
                  {name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function DecisionArchitectureCard({ brief }: { brief: GenerationBrief }) {
  const [loading, setLoading] = useState(false);
  const [ontology, setOntology] = useState<OntologyContextResponse | null>(null);

  const clientUid = brief.client_uid;
  const provenance = brief.provenance as Record<string, unknown> | undefined;
  const audience = brief.audience as Record<string, unknown> | undefined;

  // Derive a slug fallback from audience.persona_name when persona_id isn't stamped
  // (e.g. older briefs or manual-create path). "Allergy Guardian Parent" → "allergy_guardian_parent"
  const personaNameSlug = (audience?.persona_name as string || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  const personaId =
    brief.persona_id || (provenance?.source_persona_id as string) || personaNameSlug || '';

  useEffect(() => {
    if (!clientUid || !personaId) return;

    let cancelled = false;
    setLoading(true);
    agentLabApi
      .getOntologyContext(clientUid, personaId)
      .then((res) => {
        if (!cancelled)
          setOntology(normalizeOntologyContext(res as unknown as Record<string, unknown>));
      })
      .catch(() => {
        if (!cancelled) setOntology({ available: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId]);

  const concepts = ontology?.concept_activations ?? [];
  const topConcepts = concepts
    .slice()
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 4);

  return (
    <Card title="Decision architecture" icon={Network}>
      {loading ? (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
          <span className="text-[11px] text-slate-400">Loading decision graph…</span>
        </div>
      ) : !ontology || !clientUid || !personaId ? (
        <p className="text-[11px] text-slate-500">
          No persona linked — decision factors appear after a persona is selected.
        </p>
      ) : !ontology.available ? (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-[11px] text-slate-500">Cognitive graph building…</span>
        </div>
      ) : topConcepts.length === 0 ? (
        <p className="text-[11px] text-slate-500">
          No decision factors found for this persona yet.
        </p>
      ) : (
        <div className="space-y-1.5">
          {topConcepts.map((c) => {
            const colorClass =
              ENTITY_TYPE_COLORS[c.entity_type] ?? 'bg-slate-50 text-slate-600 border-slate-200';
            return (
              <div key={c.concept_id} className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wide shrink-0 ${colorClass}`}
                >
                  {c.entity_type?.slice(0, 4)}
                </span>
                <span className="text-[11px] text-slate-700 truncate">{c.display_name}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function Card({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof GitBranch;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} className="text-slate-400" />
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {title}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────────────

export function StrategicGroundingPanel({ brief, onNavigateToAngles }: Props) {
  return (
    <aside className="space-y-4">
      <PersonaThreadCard brief={brief} />
      <DecisionArchitectureCard brief={brief} />
      <BuyerScenariosCard brief={brief} onNavigateToAngles={onNavigateToAngles} />
    </aside>
  );
}
